<script setup lang="ts">
import {computed, ref} from "vue";
import {useModalStore} from "../../store/modal";
import {useAuthStore} from "../../store/auth";

interface IData {
    id?: string | null,
    title: string,
    created_at: string | null,
    content?: null | string,
    download_url?: string | null,
    url?: string | null,
}

const authStore = useAuthStore()
const modalStore = useModalStore()
const props = withDefaults(defineProps<{
    type: string,
    icon?: string,
    id: number,
    data: IData,
    read_at: string | null,

}>(), {
    icon: 'notify-active',
})

const to = computed(() => {
    // Bulk send notifications carry their own url rather than a chat id.
    if (props.type === 'App\\Notifications\\Internal\\BulkSendCompletedNotification') {
        return props.data.url ?? null
    }

    if (props.data.id) {
        switch (props.type) {
            case 'App\\Notifications\\Internal\\ChatCreatedNotification':
                return `/chats/view/${props.data.id}`
            case 'App\\Notifications\\Internal\\ChatMessageReceivedNotification':
                return `/chats/view/${props.data.chat.id}`
        }
    } else return null
})
const downloadUrl = computed(() => {
    if (props.type === 'App\\Notifications\\Internal\\DashboardReportReadyNotification') {
        return props.data.download_url ?? null
    }
    return null
})
const show_content = ref(false)
const expandable = computed(() => !!props.data.content)
const date = computed(() => {
    const raw = props?.data?.created_at
    if (!raw) return null
    const d = new Date(raw.replace(' ', 'T'))
    return isNaN(d.getTime()) ? null : d
})

const markAsRead = async () => {
    await authStore.markNotificationAsRead(props.id)
}
const handleDownload = async () => {
    if (!props.read_at) await markAsRead()
}
const handleMainClick = () => {
    if (to.value) modalStore.close()
}
</script>

<template>
    <component :is="to ? 'RouterLink' : 'div'" :to @click="handleMainClick()" class="notification-row"
               :class="{'--is-expanded':show_content}">
        <div class="notification-row-heading">
            <div class="notification-row-heading-details">
                <div class="icon-container">
                    <ah-icon :name="icon"/>
                </div>

                <div class="notification-row-heading-details-text">
                    <span>{{ data.title }}</span>
                    <span v-if="date">{{
                            new Intl.DateTimeFormat('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false,
                            }).format(date)
                        }}</span>
                </div>
            </div>
            <div class="notification-row-heading-actions">
                <button @click.stop="markAsRead" v-if="!read_at">
                    <ah-icon name="check-single"/>
                </button>
                <a v-if="downloadUrl" :href="downloadUrl" target="_blank" :download="true"
                   class="notification-row-heading-actions-download" @click.stop="handleDownload">
                    <ah-icon name="arrow-in"/>
                </a>
                <button v-if="expandable" @click.stop="show_content = !show_content">
                    <ah-icon :name="show_content ? 'chevron-down-up' : 'chevron-up-down'"/>
                </button>
            </div>
        </div>
        <div class="notification-row-content" v-if="expandable && show_content">{{ data.content }}</div>
    </component>
</template>

<style scoped lang="scss">
.notification-row {
    background: var(--color-white);
    padding: 2rem;
    border-radius: 2.4rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &-heading {
        display: flex;
        justify-content: space-between;
        align-items: center;

        &-details {
            display: flex;
            align-items: center;
            gap: 2rem;

            .icon-container {
                width: 4.8rem;
                height: 4.8rem;
                border-radius: 50%;
                background: var(--color-tiffany-dark);
                display: flex;
                justify-content: center;
                align-items: center;

                .icon {
                    fill: var(--color-white);
                }
            }

            &-text {
                display: flex;
                flex-direction: column;
                gap: 1rem;

                span {
                    @include font(1.4, 1.4, black);
                    color: var(--color-thunder);

                    &:last-child {
                        @include font(1, 1, black);
                        color: var(--color-hazy);
                    }
                }
            }
        }

        &-actions {
            display: flex;
            gap: 1rem;

            button, &-download {
                background: var(--color-white);
                width: 3.6rem;
                height: 3.6rem;
                border-radius: 50%;
                border: none;
                display: flex;
                align-items: center;
                justify-content: center;
                text-decoration: none;

                .icon {
                    fill: var(--color-hazy);
                }

                &:hover {
                    background: var(--color-raspberry);

                    .icon {
                        fill: var(--color-white);
                    }
                }
            }
        }
    }

    &-content {
        @include font(1.2, 1.8, book);
        padding-left: 6.8rem;
        color: var(--color-white);
    }

    &.--is-expanded {
        background: #006661;

        .notification-row-heading {
            &-details {
                .icon-container {
                    background: var(--color-white);

                    .icon {
                        fill: var(--color-tiffany);
                    }
                }

                &-text {
                    span:first-child {
                        color: var(--color-white);
                    }
                }
            }
        }
    }

    @include dark() {
        background: var(--color-darth-maul);

        &-heading {
            &-details {
                &-text {
                    span:first-child {
                        color: var(--color-mist);
                    }
                }
            }

            &-actions {
                button, &-download {
                    background: var(--color-darth-maul);
                }
            }
        }

        &.--is-expanded {
            background: #006661;

            .notification-row-heading {
                .notification-row-heading-details {
                    .icon-container {
                        background: var(--color-black-50);

                        .icon {
                            fill: var(--color-tiffany);
                        }
                    }
                }
            }
        }
    }
}
</style>
